import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  GetRevisionFeedbackService,
  SetRevisionInProgressService,
} from "shared/services/revision";
import { handleErrors } from "shared/utils/helper";
import { toastMessage } from "shared/components/common/toast";
import { Icons } from "assets";
import CustomPageHeading from "shared/components/common/customPageHeading";
import classNames from "classnames";
import styles from "./style.module.scss";
import moment from "moment";
import FeedbackRequestSkeleton from "shared/components/common/feedbackRequestSkeleton";
import NoContentCard from "shared/components/common/noContentCard";
import CustomButton from "shared/components/common/customButton";
import CustomGalleryView from "shared/components/common/customGalleryView";
import { routeConstant } from "shared/routes/routeConstant";
import StartWorkingConfirmationModal from "shared/modal/startWorkingConfirmation";

interface FeedbackRequestProps {
  revisionId?: number;
  selectedRevisionName?: string;
  projectId?: number;
  projectTitle?: string;
  isRevisionListLoading?: boolean;
  isInitialRevisionListLoad?: boolean;
  hasRevisions?: boolean;
  mobileFilterDropdown?: React.ReactNode;
  isArchived?: boolean;
}

const FeedbackRequest = ({
  revisionId,
  selectedRevisionName,
  projectId,
  projectTitle,
  isRevisionListLoading,
  isInitialRevisionListLoad,
  hasRevisions,
  mobileFilterDropdown,
  isArchived = false,
}: FeedbackRequestProps) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [feedbackData, setFeedbackData] = useState<any>(null);
  const [confirmStartWorking, setConfirmStartWorking] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const lastFetchedRevisionIdRef = useRef<number | undefined>(undefined);
  const isFetchingRef = useRef<boolean>(false);

  useEffect(() => {
    if (!revisionId) {
      lastFetchedRevisionIdRef.current = undefined;
      setFeedbackData(null);
      isFetchingRef.current = false;
      return;
    }

    if (
      revisionId !== lastFetchedRevisionIdRef.current &&
      !isFetchingRef.current
    ) {
      fetchFeedbackData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run when revisionId changes
  }, [revisionId]);

  const fetchFeedbackData = async () => {
    if (
      !revisionId ||
      revisionId === lastFetchedRevisionIdRef.current ||
      isFetchingRef.current
    ) {
      return;
    }

    try {
      isFetchingRef.current = true;
      setLoading(true);
      lastFetchedRevisionIdRef.current = revisionId;
      const response = await GetRevisionFeedbackService(revisionId);

      if (response.status === 200) {
        const data = response.data?.data || response.data;
        setFeedbackData(data);
      }
    } catch (error: any) {
      handleErrors(error);
      setFeedbackData(null);
      lastFetchedRevisionIdRef.current = undefined;
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  if (isRevisionListLoading && isInitialRevisionListLoad) {
    return <FeedbackRequestSkeleton />;
  }

  if (hasRevisions === false && !isRevisionListLoading) {
    return (
      <div className={classNames(styles.sectionContainer, "pt-3")}>
        <div className="flex items-center justify-center h-[600px]">
          <NoContentCard
            Icon={Icons.NoFeedbackIcon}
            label1="No revision requests yet"
            label2=""
          />
        </div>
      </div>
    );
  }

  if (!revisionId || selectedRevisionName === "Select a revision") {
    return (
      <div className={classNames(styles.sectionContainer, "pt-3")}>
        <div className="flex items-center justify-center py-8">
          <NoContentCard
            Icon={Icons.TabFeedback}
            label1="No Revision Selected"
            label2="No revision selected"
          />
        </div>
      </div>
    );
  }

  if (loading) {
    return <FeedbackRequestSkeleton />;
  }

  if (!feedbackData) {
    return (
      <div className={classNames(styles.sectionContainer, "pt-3")}>
        <div className="flex items-center justify-center h-[600px]">
          <NoContentCard
            Icon={Icons.NoFeedbackIcon}
            label1="No revision requests yet"
            label2=""
          />
        </div>
      </div>
    );
  }

  const inspirations = feedbackData.inspirations || [];
  const feedbackUser = feedbackData.feedback_user;
  const feedbackDate = feedbackData.client_feedback_date
    ? moment(feedbackData.client_feedback_date).format("MM/DD/YYYY")
    : "";

  const hasNoData =
    !feedbackData.revision_title &&
    !feedbackUser &&
    !feedbackData.client_comment &&
    (!inspirations || inspirations.length === 0);

  if (hasNoData) {
    return (
      <div className={classNames(styles.sectionContainer, "pt-3")}>
        <div className="flex items-center justify-center h-[600px]">
          <NoContentCard
            Icon={Icons.NoFeedbackIcon}
            label1="No revision requests yet"
            label2=""
          />
        </div>
      </div>
    );
  }

  const handleStartWorking = async () => {
    if (!revisionId) return;

    setConfirmStartWorking(false);
    try {
      setIsStarting(true);
      const response = await SetRevisionInProgressService(revisionId);

      if (response.status === 200 || response.status === 201) {
        toastMessage("success", "Revision marked as in progress!");
        lastFetchedRevisionIdRef.current = undefined;
        fetchFeedbackData();
      } else {
        handleErrors(response);
      }
    } catch (error: any) {
      handleErrors(error);
    } finally {
      setIsStarting(false);
    }
  };

  const getDescription = () => {
    if (!feedbackUser) {
      return undefined;
    }

    const userName =
      feedbackUser.display_name ||
      `${feedbackUser.first_name || ""} ${feedbackUser.last_name || ""}`.trim() ||
      "Unknown User";

    return `Uploaded by ${userName}${feedbackDate ? ` on ${feedbackDate}` : ""}`;
  };

  const revisionStatus =
    (feedbackData.revision_status || "").toLowerCase().replace(/[-\s]/g, "_");
  const isInReview = revisionStatus === "in_review";
  const isInProgress = revisionStatus === "in_progress";

  const startWorkingButtonTitle = isInProgress
    ? "In Progress"
    : isStarting
      ? "Starting..."
      : "Start Working";
  const isStartWorkingEnabled = isInReview && !isStarting;
  const isCompleted = revisionStatus === "completed";
  const isUploadEnabled = isInProgress || isCompleted;

  return (
    <div className={classNames(styles.sectionContainer, "")}>
      {mobileFilterDropdown}
      <div className="flex flex-col gap-5 md:mt-0 mt-4 pt-3">
        <CustomPageHeading
          title={`${feedbackData.revision_title || selectedRevisionName} Feedback`}
          size="medium"
          description={getDescription()}
          isHandleIcon={true}
        />
        {feedbackData.client_comment && (
          <CustomPageHeading
            title="Comments"
            size="medium"
            description={feedbackData.client_comment}
            renderHtml={true}
          />
        )}
        {inspirations.length > 0 && (
          <div className={styles.inspirationFilesContainer}>
            <CustomPageHeading title="Inspiration Files" size="medium" />

            <div
              className={classNames(
                styles.inspirationFilesList,
                "grid md:grid-cols-2 grid-cols-1 lg:gap-6 md:gap-5 gap-4 mt-3 mb-2",
              )}
            >
              {inspirations.map((file: any, idx: number) => (
                <div
                  key={file.revision_inspiration_id}
                  className={classNames(styles.inspirationFileItem, "")}
                >
                  <div className="flex flex-col">
                    <div className="flex gap-4">
                      <span
                        className={styles.imgContainer}
                        onClick={() => {
                          if (!file.type || file.type === "image") {
                            const imageItems = inspirations.filter(
                              (f: any) => !f.type || f.type === "image",
                            );
                            const targetIdx = imageItems.findIndex(
                              (f: any) =>
                                f.revision_inspiration_id ===
                                file.revision_inspiration_id,
                            );
                            setGalleryIndex(targetIdx >= 0 ? targetIdx : 0);
                            setGalleryOpen(true);
                          }
                        }}
                        style={{
                          cursor:
                            !file.type || file.type === "image"
                              ? "pointer"
                              : "default",
                        }}
                      >
                        {file.type && file.type !== "image" ? (
                          <a
                            href={file.path}
                            target="_blank"
                            rel="noreferrer"
                            className={styles.fileTypeThumb}
                            title="Click to view file"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Icons.FilePdfIcon />
                            <span className={styles.fileTypeThumbLabel}>
                              {file.name || "Click to view"}
                            </span>
                          </a>
                        ) : (
                          <img src={file.path} alt={file.label} />
                        )}
                      </span>
                      <CustomPageHeading
                        title="Label:"
                        description={file.label}
                        size="xsmall"
                      />
                    </div>

                    <div
                      className={classNames(
                        styles.additionalNotesContainer,
                        "xs:ml-4",
                      )}
                    >
                      <CustomPageHeading
                        title="Additional Notes: "
                        description={
                          file.additional_notes || "No additional notes"
                        }
                        size="xsmall"
                        renderHtml={true}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {!isArchived && (
        <div className="flex items-center gap-3 flex-wrap">
          <CustomButton
            Icon={Icons.Rocket}
            IconDirection="left"
            title={startWorkingButtonTitle}
            onClick={() => setConfirmStartWorking(true)}
            containerStyle="primary-btn"
            disabled={!isStartWorkingEnabled}
          />
          <CustomButton
            Icon={Icons.UploadRevisionIcon}
            IconDirection="left"
            title="Upload Revision"
            onClick={() =>
              navigate(
                `${routeConstant.designer.home.uploadRevision.path}?projectId=${projectId}`,
                {
                  state: { projectId, projectTitle },
                },
              )
            }
            containerStyle="primary-btn"
            disabled={!isUploadEnabled}
          />
        </div>
        )}
      </div>
      <StartWorkingConfirmationModal
        show={confirmStartWorking}
        handleClose={() => setConfirmStartWorking(false)}
        handleSubmit={handleStartWorking}
        loading={isStarting}
      />
      <CustomGalleryView
        show={galleryOpen}
        handleClose={() => setGalleryOpen(false)}
        items={inspirations
          .filter((f: any) => !f.type || f.type === "image")
          .map((f: any) => ({
            id: String(f.revision_inspiration_id),
            src: f.path,
            name: f.label || "",
            alt: f.label || "",
          }))}
        initialIndex={galleryIndex}
      />
    </div>
  );
};

export default FeedbackRequest;
